
import { useState, useEffect } from 'react';
import { db } from '@/lib/firebase';
import {
    collection,
    query,
    where,
    onSnapshot,
    doc,
    setDoc,
    getDoc,
    updateDoc,
    increment,
    serverTimestamp,
    Timestamp,
    deleteDoc,
    orderBy,
    collectionGroup
} from 'firebase/firestore';

export interface ETFPosition {
    id: string; // etfId
    name: string;
    ticker: string;
    investedAmount: number; // KRW
    quantity: number; // investedAmount / purchasePrice
    avgPrice: number;
    updatedAt: Timestamp;
}

export function useUserETF(userId?: string) {
    const [positions, setPositions] = useState<ETFPosition[]>([]);
    const [allPositions, setAllPositions] = useState<(ETFPosition & { userId: string })[]>([]); // Admin용 추가
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!userId) {
            setLoading(false);
            return;
        }
        const q = query(collection(db, 'users', userId, 'etf_positions'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const items = snapshot.docs.map(d => ({
                id: d.id,
                ...d.data()
            })) as ETFPosition[];
            setPositions(items);
            setLoading(false);
        });
        return () => unsubscribe();
    }, [userId]);

    // Subscribe to all users' ETF positions (Admin)
    useEffect(() => {
        const q = query(collectionGroup(db, 'etf_positions'), orderBy('updatedAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const items = snapshot.docs.map(d => ({
                id: d.id,
                userId: d.ref.parent.parent?.id || 'unknown',
                ...d.data()
            })) as (ETFPosition & { userId: string })[];
            setAllPositions(items);
        });
        return () => unsubscribe();
    }, []);

    const recordInvestment = async (etfId: string, etfName: string, ticker: string, amount: number, currentPrice: number) => {
        if (!userId) throw new Error("User not authenticated");
        const posRef = doc(db, 'users', userId, 'etf_positions', etfId);
        const posSnap = await getDoc(posRef);

        const quantity = amount / currentPrice;

        if (posSnap.exists()) {
            const data = posSnap.data() as ETFPosition;
            const newTotalAmount = data.investedAmount + amount;
            const newTotalQuantity = data.quantity + quantity;
            const newAvgPrice = newTotalAmount / newTotalQuantity;

            await updateDoc(posRef, {
                investedAmount: increment(amount),
                quantity: increment(quantity),
                avgPrice: newAvgPrice,
                updatedAt: serverTimestamp()
            });
        } else {
            await setDoc(posRef, {
                name: etfName,
                ticker: ticker,
                investedAmount: amount,
                quantity: quantity,
                avgPrice: currentPrice,
                updatedAt: serverTimestamp()
            });
        }
    };

    const sellETF = async (etfId: string, sellQuantity: number, currentPrice: number, targetUserId: string = userId!) => {
        if (!targetUserId) throw new Error("User not authenticated");
        const posRef = doc(db, 'users', targetUserId, 'etf_positions', etfId);
        const posSnap = await getDoc(posRef);
        if (!posSnap.exists()) return;

        const data = posSnap.data() as ETFPosition;
        const refundAmount = sellQuantity * currentPrice;

        if (data.quantity <= sellQuantity) {
            await deleteDoc(posRef);
        } else {
            await updateDoc(posRef, {
                quantity: increment(-sellQuantity),
                investedAmount: increment(- (sellQuantity * data.avgPrice)),
                updatedAt: serverTimestamp()
            });
        }

        const userDocRef = doc(db, 'users', targetUserId);
        await updateDoc(userDocRef, {
            krw: increment(refundAmount)
        });
    };

    return { positions, allPositions, loading, recordInvestment, sellETF };
}
